import React, { useState } from 'react';
import { 
  Paper, 
  Typography, 
  Box, 
  Button, 
  Stack,
  Alert
} from '@mui/material';
import { Code as CodeIcon, ArrowDownward as ArrowDownwardIcon } from '@mui/icons-material';
import { useBuild } from '../contexts/BuildContext';
import QueryDialog from './QueryDialog';
import BackendCallDialog from '../components/BackendCallDialog';

const InfoColumn = () => {
  const { searchResults, searchLastCall } = useBuild();
  const [queryDialogOpen, setQueryDialogOpen] = useState(false);
  const [backendCallDialogOpen, setBackendCallDialogOpen] = useState(false);

  const handleOpenQueryDialog = () => {
    setQueryDialogOpen(true);
  };

  const handleCloseQueryDialog = () => {
    setQueryDialogOpen(false);
  };

  const handleOpenBackendCallDialog = () => {
    setBackendCallDialogOpen(true);
  };

  const handleCloseBackendCallDialog = () => {
    setBackendCallDialogOpen(false);
  };

  const renderStatistics = () => {
    // Show error message if there's an error in searchResults
    if (searchResults?.error) {
      return (
        <Stack spacing={2}>
          <Alert severity="error">
            <Typography variant="subtitle2" gutterBottom>
              Query Error
            </Typography>
            <Typography variant="body2">
              {searchResults.error}
            </Typography>
          </Alert>
          {searchResults?.query && (
            <Button
              variant="outlined"
              startIcon={<CodeIcon />}
              onClick={handleOpenQueryDialog}
              fullWidth
            >
              View ES Query
            </Button>
          )}
        </Stack>
      );
    }

    if (!searchResults || !searchResults.result) {
      return (
        <Typography variant="body2" color="text.secondary">
          No query executed yet.
        </Typography>
      );
    }

    const result = searchResults.result;
    const totalHits = result.total_hits || 0;
    const fetchedHits = result.hits?.length || 0;
    const tookEs = result.took_es || 0;
    const tookBackend = result.took_backend || 0;
    const tookFrontend = searchResults.took_frontend || 0;
    const maxScore = result.max_score || 0;

    return (
      <Stack spacing={2}>
        {/* Backend Call Button */}
        <Button
          variant="outlined"
          startIcon={<CodeIcon />}
          onClick={handleOpenBackendCallDialog}
          fullWidth
          disabled={!searchLastCall}
        >
          Backend Call
        </Button>

        {/* Arrow indicator */}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <ArrowDownwardIcon color="action" />
        </Box>

        <Box>
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Total Hits:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {totalHits}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Docs Fetched:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {fetchedHits}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">ES Time:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {tookEs}ms
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Backend Time:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {tookBackend}ms
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Frontend Time:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {tookFrontend}ms
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Max Score:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {maxScore}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {searchResults?.query && (
          <Button
            variant="outlined"
            startIcon={<CodeIcon />}
            onClick={handleOpenQueryDialog}
            fullWidth
          >
            View ES Query
          </Button>
        )}
      </Stack>
    );
  };

  return (
    <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h6" gutterBottom>
        Query Information
      </Typography>
      
      <Box sx={{ mt: 2, flex: 1, overflow: 'auto' }}>
        {renderStatistics()}
      </Box>

      {/* Query Dialog */}
      <QueryDialog
        open={queryDialogOpen}
        onClose={handleCloseQueryDialog}
        title="Elasticsearch Query"
        queryData={searchResults?.query}
        maxWidth="md"
      />

      {/* Backend Call Dialog */}
      <BackendCallDialog
        open={backendCallDialogOpen}
        onClose={handleCloseBackendCallDialog}
        apiCall={searchLastCall}
        maxWidth="md"
      />
    </Paper>
  );
};

export default InfoColumn;
